"use client";

import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { cn } from "@/lib/utils";
import Link from "next/link";
import Image from "next/image";

type BlogPostProps = {
  title: string;
  authorName: string;
  authorAvatarUrl?: string;
  coverImageUrl?: string;
  publishedAt: string | Date;
  richText: unknown;
  className?: string;
};

type HeadingItem = {
  id: string;
  text: string;
  level: number;
  element: HTMLElement;
};

const WORDS_PER_MIN = 200;

function slugify(input: string): string {
  const diacriticsMap: Record<string, string> = {
    ą: "a",
    ć: "c",
    ę: "e",
    ł: "l",
    ń: "n",
    ó: "o",
    ś: "s",
    ź: "z",
    ż: "z",
    Ą: "A",
    Ć: "C",
    Ę: "E",
    Ł: "L",
    Ń: "N",
    Ó: "O",
    Ś: "S",
    Ź: "Z",
    Ż: "Z",
    à: "a",
    á: "a",
    â: "a",
    ã: "a",
    ä: "a",
    å: "a",
    è: "e",
    é: "e",
    ê: "e",
    ë: "e",
    ì: "i",
    í: "i",
    î: "i",
    ï: "i",
    ò: "o",
    ô: "o",
    õ: "o",
    ö: "o",
    ù: "u",
    ú: "u",
    û: "u",
    ü: "u",
    ý: "y",
    ÿ: "y",
    ñ: "n",
    ç: "c",
  };

  let result = input
    .split("")
    .map((char) => diacriticsMap[char] || char)
    .join("")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\s/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

  if (result.length > 80) {
    result = result.substring(0, 80).replace(/-[^-]*$/, "");
  }

  return result;
}

function formatDate(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function computeReadingTime(text: string, wpm = WORDS_PER_MIN): number {
  const words = text.trim().split(/\s+/).length;
  const minutes = Math.ceil(words / wpm);
  return Math.max(1, minutes);
}

function processRichTextWithIds(richText: unknown): {
  processedHtml: string;
  headings: { id: string; text: string; level: number }[];
} {
  if (typeof richText === "string") {
    if (typeof window === "undefined") {
      return {
        processedHtml: richText,
        headings: [],
      };
    }

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = richText;

    const headings: { id: string; text: string; level: number }[] = [];
    const headingElements = tempDiv.querySelectorAll("h1, h2, h3, h4");

    headingElements.forEach((el) => {
      const htmlEl = el as HTMLElement;
      const text = htmlEl.textContent?.trim() || "";

      if (text) {
        const level = parseInt(htmlEl.tagName.substring(1));
        let id = htmlEl.id;

        if (!id) {
          id = slugify(text);
          htmlEl.id = id;
        }

        headings.push({ id, text, level });
      }
    });

    return {
      processedHtml: tempDiv.innerHTML,
      headings,
    };
  } else if (richText && typeof richText === "object") {
    const demoHeadings = [
      {
        id: "introduction-to-strategic-design",
        text: "Introduction to Strategic Design",
        level: 2,
      },
      {
        id: "the-evolution-of-design-thinking",
        text: "The Evolution of Design Thinking",
        level: 2,
      },
      {
        id: "key-principles-of-strategic-design",
        text: "Key Principles of Strategic Design",
        level: 3,
      },
      {
        id: "implementation-strategies",
        text: "Implementation Strategies",
        level: 3,
      },
      {
        id: "building-design-capabilities",
        text: "Building Design Capabilities",
        level: 3,
      },
      {
        id: "measuring-design-impact",
        text: "Measuring Design Impact",
        level: 3,
      },
      { id: "future-implications", text: "Future Implications", level: 2 },
      { id: "emerging-trends", text: "Emerging Trends", level: 3 },
      {
        id: "ai-and-machine-learning",
        text: "AI and Machine Learning",
        level: 4,
      },
      {
        id: "sustainability-considerations",
        text: "Sustainability Considerations",
        level: 4,
      },
      {
        id: "inclusive-and-accessible-design",
        text: "Inclusive and Accessible Design",
        level: 4,
      },
      { id: "conclusion", text: "Conclusion", level: 2 },
    ];

    const demoHtml = `
      <p class="text-text-p2 italic mb-4">
        Builder.io content detected. In production, this would render with BuilderContent component.
      </p>
      <div>
        <h2 id="introduction-to-strategic-design">Introduction to Strategic Design</h2>
        <p>
          Strategic design represents a fundamental shift in how we approach design thinking and implementation in modern
          organizations. This comprehensive guide explores the evolution of design thinking from a creative process to a strategic business methodology.
        </p>

        <h2 id="the-evolution-of-design-thinking">The Evolution of Design Thinking</h2>
        <p>
          Over the past decade, design thinking has evolved from a methodology primarily used by designers to a strategic approach
          adopted by organizations worldwide. Companies now recognize design as a core business function that drives innovation and competitive advantage.
        </p>

        <h3 id="key-principles-of-strategic-design">Key Principles of Strategic Design</h3>
        <p>The core principles that drive strategic design include user-centricity, systems thinking, and iterative improvement. These principles ensure that design decisions align with business objectives while maintaining focus on user needs.</p>

        <h3 id="implementation-strategies">Implementation Strategies</h3>
        <p>Successful implementation requires careful planning, stakeholder alignment, and continuous measurement of outcomes. Organizations must develop clear processes and frameworks to integrate design thinking into their operational workflows.</p>

        <h3 id="building-design-capabilities">Building Design Capabilities</h3>
        <p>Organizations need to invest in building internal design capabilities through training, hiring, and creating cross-functional teams. This involves developing both hard skills and design mindsets across the organization.</p>

        <h3 id="measuring-design-impact">Measuring Design Impact</h3>
        <p>Quantifying the impact of design initiatives is crucial for demonstrating value and securing continued investment. Organizations use various metrics including user satisfaction, conversion rates, and business outcomes to measure design effectiveness.</p>

        <h2 id="future-implications">Future Implications</h2>
        <p>
          As we look toward the future, strategic design will continue to play an increasingly important role in organizational
          success. The integration of new technologies and changing user expectations will drive further evolution in design practices.
        </p>

        <h3 id="emerging-trends">Emerging Trends</h3>
        <p>
          Several emerging trends are shaping the future of strategic design, including AI integration, sustainability focus, and
          inclusive design practices. These trends reflect broader societal shifts and technological advancements.
        </p>

        <h4 id="ai-and-machine-learning">AI and Machine Learning</h4>
        <p>Artificial intelligence is revolutionizing how we approach design challenges and opportunities. AI tools are augmenting designers' capabilities and enabling new forms of personalization and automation in design processes.</p>

        <h4 id="sustainability-considerations">Sustainability Considerations</h4>
        <p>Environmental and social sustainability are becoming central to strategic design decisions. Organizations are increasingly considering the long-term impact of their design choices on both users and the planet.</p>

        <h4 id="inclusive-and-accessible-design">Inclusive and Accessible Design</h4>
        <p>Creating designs that work for everyone, regardless of ability or background, is becoming a core requirement rather than an afterthought. This shift reflects growing awareness of diversity and inclusion in design practices.</p>

        <h2 id="conclusion">Conclusion</h2>
        <p>
          Strategic design represents a powerful approach to creating meaningful solutions that serve both user needs and business objectives. As organizations continue to embrace design thinking, the future holds tremendous potential for innovation and positive impact.
        </p>
      </div>
    `;

    return {
      processedHtml: demoHtml,
      headings: demoHeadings,
    };
  }

  return {
    processedHtml: "<div>No content available</div>",
    headings: [],
  };
}

function useHeadings(
  headingsData: { id: string; text: string; level: number }[],
  rootEl: HTMLElement | null,
): HeadingItem[] {
  const [headings, setHeadings] = useState<HeadingItem[]>([]);

  useEffect(() => {
    if (!rootEl || !headingsData.length) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHeadings([]);
      return;
    }

    const items: HeadingItem[] = [];

    headingsData.forEach(({ id, text, level }) => {
      const element = rootEl.querySelector(`#${id}`) as HTMLElement;
      if (element) {
        items.push({
          id,
          text,
          level,
          element,
        });
      }
    });

    setHeadings(items);
  }, [headingsData, rootEl]);

  return headings;
}

function useScrollSpy(headings: HeadingItem[]): string {
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    if (!headings.length || typeof window === "undefined") return;

    const handleScroll = () => {
      const firstHeading = headings[0];
      if (!firstHeading || !firstHeading.element) {
        return;
      }

      const hasContent =
        firstHeading.element.textContent &&
        firstHeading.element.textContent.trim().length > 0;

      if (!hasContent) {
        return;
      }

      let currentActiveId = "";
      const scrollY = window.scrollY;

      for (let i = 0; i < headings.length; i++) {
        const heading = headings[i];

        const docElement = document.getElementById(heading.id);
        const element = docElement || heading.element;

        const offsetTop = element.offsetTop;
        const elementTop = offsetTop - scrollY;

        if (offsetTop > 0 && elementTop <= 300) {
          currentActiveId = heading.id;
        } else if (offsetTop > 0) {
          break;
        }
      }

      if (!currentActiveId && headings.length > 0) {
        currentActiveId = headings[0].id;
      }

      if (currentActiveId !== activeId) {
        setActiveId(currentActiveId);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });

    const initScrollSpy = () => {
      const firstHeading = headings[0];
      if (firstHeading && firstHeading.element) {
        const hasContent =
          firstHeading.element.textContent &&
          firstHeading.element.textContent.trim().length > 0;

        if (hasContent) {
          handleScroll();
        } else {
          setTimeout(initScrollSpy, 100);
        }
      } else {
        setTimeout(initScrollSpy, 100);
      }
    };

    setTimeout(initScrollSpy, 50);

    const cleanup = () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };

    return cleanup;
  }, [headings, activeId]);

  return activeId;
}

function TableOfContentsSkeleton({ mobile = false }: { mobile?: boolean }) {
  const skeletonItems = [
    { level: 2, width: "w-3/4" },
    { level: 2, width: "w-full" },
    { level: 3, width: "w-5/6" },
    { level: 3, width: "w-2/3" },
    { level: 3, width: "w-4/5" },
    { level: 2, width: "w-3/5" },
    { level: 3, width: "w-3/4" },
    { level: 4, width: "w-1/2" },
  ];

  const skeletonContent = (
    <nav className="space-y-1">
      {skeletonItems.map((item, index) => {
        const indentClass =
          item.level === 3 ? "pl-4" : item.level === 4 ? "pl-8" : "";

        return (
          <div
            key={index}
            className={`relative block py-2 px-3 rounded-md ${indentClass}`}
          >
            <div
              className={`h-4 bg-bg-4 rounded animate-shimmer ${item.width}`}
            />
          </div>
        );
      })}
    </nav>
  );

  if (mobile) {
    return (
      <details className="group mb-8 border border-bg-4 rounded-lg" open>
        {/* oxlint-disable-next-line jsx-a11y/control-has-associated-label -- the summary's text sits in nested spans */}
        <summary className="cursor-pointer px-4 py-3 font-semibold text-sm uppercase tracking-wider text-text-p2 list-none">
          <div className="flex items-center justify-between">
            <span>Table of Contents</span>
            <span className="opacity-50">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </span>
          </div>
        </summary>
        <div className="px-4 pb-4">{skeletonContent}</div>
      </details>
    );
  }

  return (
    <div className="sticky top-24">
      <h3 className="text-xs font-semibold tracking-wider text-text-subhead uppercase border-b border-bg-4 pb-3 mb-4">
        Table of Contents
      </h3>
      {skeletonContent}
    </div>
  );
}

function TableOfContents({
  items,
  activeId,
  onLinkClick,
  mobile = false,
}: {
  items: HeadingItem[];
  activeId: string;
  onLinkClick: (id: string) => void;
  mobile?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const handleLinkClick = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    onLinkClick(id);
    if (mobile) {
      setIsOpen(false);
    }
  };

  const tocContent = (
    <nav className="space-y-1">
      {items.map(({ id, text, level }) => {
        const isActive = activeId === id;
        const indentClass = level === 3 ? "pl-4" : level === 4 ? "pl-8" : "";

        return (
          <Link
            key={id}
            href={`#${id}`}
            onClick={(e) => handleLinkClick(e, id)}
            className={cn(
              "relative block py-2 px-3 text-sm transition-all duration-200 rounded-md",
              indentClass,
              isActive
                ? "text-text-heading font-semibold bg-accent-1/10 border-l-2 border-accent-3 ml-0 pl-2"
                : "text-text-p2 hover:text-text-heading hover:bg-bg-4/50",
            )}
            aria-current={isActive ? "true" : undefined}
          >
            {text}
          </Link>
        );
      })}
    </nav>
  );

  if (mobile) {
    return (
      <details
        className="group mb-8 border border-bg-4 rounded-lg"
        open={isOpen}
        onToggle={(e) => setIsOpen((e.target as HTMLDetailsElement).open)}
      >
        {/* oxlint-disable-next-line jsx-a11y/control-has-associated-label -- the summary's text sits in nested spans */}
        <summary className="cursor-pointer px-4 py-3 font-semibold text-sm uppercase tracking-wider text-text-p2 focus-visible:outline-2 focus-visible:outline-accent-3 list-none">
          <div className="flex items-center justify-between">
            <span>Table of Contents</span>
            <span className="transition-transform group-open:rotate-45">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </span>
          </div>
        </summary>
        <div className="px-4 pb-4">{tocContent}</div>
      </details>
    );
  }

  return (
    <div className="sticky top-24">
      <h3 className="text-xs font-semibold tracking-wider text-text-subhead uppercase border-b border-bg-4 pb-3 mb-4">
        Table of Contents
      </h3>
      {tocContent}
    </div>
  );
}

function PostHeader({
  title,
  authorName,
  authorAvatarUrl,
  publishedAt,
  coverImageUrl,
  readingTime,
}: {
  title: string;
  authorName: string;
  authorAvatarUrl?: string;
  publishedAt: string | Date;
  coverImageUrl?: string;
  readingTime: number;
}) {
  return (
    <header className="mb-8 font-space">
      <h1 className="text-style-heading-2 md:text-style-heading-1 font-bold tracking-tight text-text-heading mb-6">
        {title}
      </h1>

      <div className="flex items-center gap-1.5 text-sm text-text-p2 mb-6">
        {authorAvatarUrl && (
          <Image
            src={authorAvatarUrl}
            alt={authorName}
            width={32}
            height={32}
            className="w-8 h-8 rounded-full object-cover mr-1"
            unoptimized={
              authorAvatarUrl.includes("unsplash.com") ||
              authorAvatarUrl.includes("placeholder")
            }
          />
        )}
        <span className="font-medium text-text-heading">{authorName}</span>
        <span>•</span>
        <time
          dateTime={
            typeof publishedAt === "string"
              ? publishedAt
              : publishedAt.toISOString()
          }
        >
          {formatDate(publishedAt)}
        </time>
        <span>•</span>
        <span>{readingTime} min read</span>
      </div>

      {coverImageUrl && (
        <div className="mt-6">
          <Image
            src={coverImageUrl}
            alt=""
            width={800}
            height={450}
            className="w-full aspect-video object-cover rounded-2xl shadow-sm"
            unoptimized={
              coverImageUrl.includes("unsplash.com") ||
              coverImageUrl.includes("placeholder")
            }
          />
        </div>
      )}
    </header>
  );
}

function PostContent({
  processedHtml,
  onReady,
}: {
  processedHtml: string;
  onReady?: (root: HTMLElement) => void;
}) {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current && onReady) {
      onReady(contentRef.current);
    }
  }, [onReady, processedHtml]);

  return (
    <div
      ref={contentRef}
      className="post-content space-y-6 leading-relaxed text-style-paragraph rich-text"
      dangerouslySetInnerHTML={{ __html: processedHtml }}
    />
  );
}

export function BlogPost({
  title,
  authorName,
  authorAvatarUrl,
  coverImageUrl,
  publishedAt,
  richText,
  className,
}: BlogPostProps): React.JSX.Element {
  const [contentRoot, setContentRoot] = useState<HTMLElement | null>(null);
  const [readingTime, setReadingTime] = useState<number>(1);
  const [isClientSide, setIsClientSide] = useState(false);
  const flashTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const highlightedElementsRef = useRef<HTMLElement[]>([]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsClientSide(true);
  }, []);

  const { processedHtml, headings: headingsData } = useMemo(() => {
    if (!isClientSide) {
      if (typeof richText === "string") {
        return { processedHtml: richText, headings: [] };
      } else if (richText && typeof richText === "object") {
        const demoHeadings = [
          {
            id: "introduction-to-strategic-design",
            text: "Introduction to Strategic Design",
            level: 2,
          },
          {
            id: "the-evolution-of-design-thinking",
            text: "The Evolution of Design Thinking",
            level: 2,
          },
          {
            id: "key-principles-of-strategic-design",
            text: "Key Principles of Strategic Design",
            level: 3,
          },
          {
            id: "implementation-strategies",
            text: "Implementation Strategies",
            level: 3,
          },
          {
            id: "building-design-capabilities",
            text: "Building Design Capabilities",
            level: 3,
          },
          {
            id: "measuring-design-impact",
            text: "Measuring Design Impact",
            level: 3,
          },
          { id: "future-implications", text: "Future Implications", level: 2 },
          { id: "emerging-trends", text: "Emerging Trends", level: 3 },
          {
            id: "ai-and-machine-learning",
            text: "AI and Machine Learning",
            level: 4,
          },
          {
            id: "sustainability-considerations",
            text: "Sustainability Considerations",
            level: 4,
          },
          {
            id: "inclusive-and-accessible-design",
            text: "Inclusive and Accessible Design",
            level: 4,
          },
          { id: "conclusion", text: "Conclusion", level: 2 },
        ];

        const demoHtml = `
          <p class="text-text-p2 italic mb-4">
            Builder.io content detected. In production, this would render with BuilderContent component.
          </p>
          <div>
            <h2 id="introduction-to-strategic-design">Introduction to Strategic Design</h2>
            <p>
              Strategic design represents a fundamental shift in how we approach design thinking and implementation in modern
              organizations. This comprehensive guide explores the evolution of design thinking from a creative process to a strategic business methodology.
            </p>

            <h2 id="the-evolution-of-design-thinking">The Evolution of Design Thinking</h2>
            <p>
              Over the past decade, design thinking has evolved from a methodology primarily used by designers to a strategic approach
              adopted by organizations worldwide. Companies now recognize design as a core business function that drives innovation and competitive advantage.
            </p>

            <h3 id="key-principles-of-strategic-design">Key Principles of Strategic Design</h3>
            <p>The core principles that drive strategic design include user-centricity, systems thinking, and iterative improvement. These principles ensure that design decisions align with business objectives while maintaining focus on user needs.</p>

            <h3 id="implementation-strategies">Implementation Strategies</h3>
            <p>Successful implementation requires careful planning, stakeholder alignment, and continuous measurement of outcomes. Organizations must develop clear processes and frameworks to integrate design thinking into their operational workflows.</p>

            <h3 id="building-design-capabilities">Building Design Capabilities</h3>
            <p>Organizations need to invest in building internal design capabilities through training, hiring, and creating cross-functional teams. This involves developing both hard skills and design mindsets across the organization.</p>

            <h3 id="measuring-design-impact">Measuring Design Impact</h3>
            <p>Quantifying the impact of design initiatives is crucial for demonstrating value and securing continued investment. Organizations use various metrics including user satisfaction, conversion rates, and business outcomes to measure design effectiveness.</p>

            <h2 id="future-implications">Future Implications</h2>
            <p>
              As we look toward the future, strategic design will continue to play an increasingly important role in organizational
              success. The integration of new technologies and changing user expectations will drive further evolution in design practices.
            </p>

            <h3 id="emerging-trends">Emerging Trends</h3>
            <p>
              Several emerging trends are shaping the future of strategic design, including AI integration, sustainability focus, and
              inclusive design practices. These trends reflect broader societal shifts and technological advancements.
            </p>

            <h4 id="ai-and-machine-learning">AI and Machine Learning</h4>
            <p>Artificial intelligence is revolutionizing how we approach design challenges and opportunities. AI tools are augmenting designers' capabilities and enabling new forms of personalization and automation in design processes.</p>

            <h4 id="sustainability-considerations">Sustainability Considerations</h4>
            <p>Environmental and social sustainability are becoming central to strategic design decisions. Organizations are increasingly considering the long-term impact of their design choices on both users and the planet.</p>

            <h4 id="inclusive-and-accessible-design">Inclusive and Accessible Design</h4>
            <p>Creating designs that work for everyone, regardless of ability or background, is becoming a core requirement rather than an afterthought. This shift reflects growing awareness of diversity and inclusion in design practices.</p>

            <h2 id="conclusion">Conclusion</h2>
            <p>
              Strategic design represents a powerful approach to creating meaningful solutions that serve both user needs and business objectives. As organizations continue to embrace design thinking, the future holds tremendous potential for innovation and positive impact.
            </p>
          </div>
        `;

        return {
          processedHtml: demoHtml,
          headings: demoHeadings,
        };
      }
      return { processedHtml: "<div>No content available</div>", headings: [] };
    }

    return processRichTextWithIds(richText);
  }, [richText, isClientSide]);

  const headings = useHeadings(headingsData, contentRoot);
  const activeId = useScrollSpy(headings);

  const handleContentReady = useCallback((root: HTMLElement) => {
    setContentRoot(root);
  }, []);

  useEffect(() => {
    if (!contentRoot) return;

    const text = contentRoot.innerText || "";
    const time = computeReadingTime(text);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setReadingTime(time);
  }, [contentRoot, processedHtml]);

  const handleTocLinkClick = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (!element) return;

    const prefersReducedMotion =
      typeof window !== "undefined"
        ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
        : false;

    if (!prefersReducedMotion) {
      if (flashTimeoutRef.current) {
        clearTimeout(flashTimeoutRef.current);
      }
      highlightedElementsRef.current.forEach((el) => {
        if (el && el.classList) {
          el.classList.add("removing");
          setTimeout(() => {
            el.classList.remove("section-flash", "removing");
          }, 400);
        }
      });

      highlightedElementsRef.current = [];

      const currentLevel = parseInt(element.tagName.substring(1));
      const elementsToHighlight: HTMLElement[] = [];
      let currentElement: Element | null = element;

      elementsToHighlight.push(element);

      while (currentElement && currentElement.nextElementSibling) {
        currentElement = currentElement.nextElementSibling;

        if (currentElement.tagName.match(/^H[1-4]$/)) {
          const nextLevel = parseInt(currentElement.tagName.substring(1));
          if (nextLevel <= currentLevel) {
            break;
          }
        }

        elementsToHighlight.push(currentElement as HTMLElement);
      }

      highlightedElementsRef.current = elementsToHighlight;

      setTimeout(() => {
        elementsToHighlight.forEach((el) => {
          if (el && el.classList) {
            el.classList.add("section-flash");
          }
        });
      }, 50);

      flashTimeoutRef.current = setTimeout(() => {
        elementsToHighlight.forEach((el) => {
          if (el && el.classList) {
            el.classList.add("removing");
            setTimeout(() => {
              el.classList.remove("section-flash", "removing");
            }, 400);
          }
        });
        highlightedElementsRef.current = [];
      }, 2000);
    }

    element.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    });

    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#${id}`);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current) {
        clearTimeout(flashTimeoutRef.current);
      }
      highlightedElementsRef.current.forEach((el) => {
        if (el && el.classList) {
          el.classList.add("removing");
          setTimeout(() => {
            el.classList.remove("section-flash", "removing");
          }, 400);
        }
      });
    };
  }, []);

  return (
    <article
      className={cn(
        "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 font-sans",
        className,
      )}
    >
      <div className="lg:grid lg:grid-cols-[240px_minmax(0,1fr)] xl:grid-cols-[240px_minmax(0,1fr)_minmax(0,200px)] lg:gap-10">
        <aside className="hidden lg:block">
          {!isClientSide ? (
            <TableOfContentsSkeleton />
          ) : headings.length > 0 ? (
            <TableOfContents
              items={headings}
              activeId={activeId}
              onLinkClick={handleTocLinkClick}
            />
          ) : null}
        </aside>

        <div className="min-w-0 max-w-4xl lg:max-w-none">
          <PostHeader
            title={title}
            authorName={authorName}
            authorAvatarUrl={authorAvatarUrl}
            publishedAt={publishedAt}
            coverImageUrl={coverImageUrl}
            readingTime={readingTime}
          />

          <div className="lg:hidden">
            {!isClientSide ? (
              <TableOfContentsSkeleton mobile />
            ) : headings.length > 0 ? (
              <TableOfContents
                items={headings}
                activeId={activeId}
                onLinkClick={handleTocLinkClick}
                mobile
              />
            ) : null}
          </div>

          <PostContent
            processedHtml={processedHtml}
            onReady={handleContentReady}
          />
        </div>

        <div className="hidden lg:block"></div>
      </div>
    </article>
  );
}
